// Typed client for the brand-mint admin API. Every call is SAME-ORIGIN: the
// console is served on :8090 and nginx (prod) / the vite dev proxy (dev)
// forwards /v1/* to the backend on :8300. The access token is sent as a Bearer.
import { userManager } from "./auth";

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  enabled: boolean;
  roles: string[];
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  temporary?: boolean;
}

export interface CreateUserResponse {
  id: string;
}

// ApiError carries the HTTP status so views can render 401/403 distinctly.
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function bearer(): Promise<string> {
  const user = await userManager.getUser();
  if (!user || !user.access_token) {
    throw new ApiError(401, "Not signed in.");
  }
  return user.access_token;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await bearer();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const resp = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!resp.ok) {
    throw new ApiError(resp.status, await errorMessage(resp));
  }
  // Some POSTs may return 201 with a body; DELETE-like calls may be empty.
  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

async function errorMessage(resp: Response): Promise<string> {
  try {
    const data = (await resp.json()) as { error?: string; message?: string };
    if (data.error) return data.error;
    if (data.message) return data.message;
  } catch {
    // fall through to a generic message
  }
  switch (resp.status) {
    case 401:
      return "Unauthorized — your session is missing or expired. Please sign in again.";
    case 403:
      return "Forbidden — your account is not authorized for this action.";
    default:
      return `Request failed (HTTP ${resp.status}).`;
  }
}

export function listUsers(): Promise<AdminUser[]> {
  // Defensive normalization: guarantee every user has a roles array so the UI
  // can read user.roles.length without a nullish crash, even if the backend
  // ever omits/nulls the field.
  return request<AdminUser[]>("GET", "/v1/admin/users").then((u) =>
    (u ?? []).map((x) => ({ ...x, roles: x.roles ?? [] })),
  );
}

export function createUser(
  req: CreateUserRequest,
): Promise<CreateUserResponse> {
  return request<CreateUserResponse>("POST", "/v1/admin/users", req);
}

// getOpenApiSpec fetches the backend's embedded OpenAPI document (bearer-gated)
// for the Swagger tab to render. Returns the parsed spec object.
export function getOpenApiSpec(): Promise<object> {
  return request<object>("GET", "/v1/openapi.json");
}

export function listRoles(): Promise<string[]> {
  return request<string[]>("GET", "/v1/admin/roles").then((r) => r ?? []);
}

export function assignRoles(userId: string, roles: string[]): Promise<void> {
  return request<void>(
    "POST",
    `/v1/admin/users/${encodeURIComponent(userId)}/roles`,
    { roles },
  );
}

// --- Brand provisioning (the create → ceremony → activate → deliver workflow) ---
// Wire fields are kebab-case, matching the backend JSON contract.

export interface Brand {
  "brand-id": string;
  namespace: string;
  "account-id": string;
  tenant: string;
  "display-name": string;
  "snap-ids": Record<string, string>;
  status: string; // "pending-ceremony" | "active"
  "created-at": string;
  "activated-at"?: string;
  "ceremony-chain"?: string;
  // Whether the ceremony chain was cryptographically verified (vs merely
  // submitted + structurally checked). Currently always false — full
  // verification is a backend follow-up.
  "chain-verified"?: boolean;
}

// CeremonyPack is the POST /v1/brands response: the new brand + the coc-mint
// instruction the operator runs on their own box.
export interface CeremonyPack extends Brand {
  ceremony: { tool: string; command: string; note: string };
}

export interface MintBrandRequest {
  namespace: string;
  tenant: string;
  "display-name": string;
}

export interface KitMeta {
  "kit-id": string;
  "brand-id": string;
  namespace: string;
  tenant: string;
  version: string;
  "created-at": string;
}

export interface BuildKitRequest {
  domain: string;
  "store-base-url"?: string;
  tls?: string; // internal | acme
}

export interface KitResponse {
  "kit-id": string;
  "brand-id": string;
  tenant: string;
  version: string;
  "created-at": string;
  download: string;
  manifest: string;
}

export function listBrands(): Promise<Brand[]> {
  return request<Brand[]>("GET", "/v1/brands").then((b) => b ?? []);
}

export function mintBrand(req: MintBrandRequest): Promise<CeremonyPack> {
  return request<CeremonyPack>("POST", "/v1/brands", req);
}

export function activateBrand(id: string, assertions: string): Promise<Brand> {
  return request<Brand>(
    "POST",
    `/v1/brands/${encodeURIComponent(id)}/activate`,
    { assertions },
  );
}

// deleteBrand removes a brand. The backend refuses (409) if it still has kits.
export function deleteBrand(id: string): Promise<void> {
  return request<void>("DELETE", `/v1/brands/${encodeURIComponent(id)}`);
}

export function listKits(brandId: string): Promise<KitMeta[]> {
  return request<KitMeta[]>(
    "GET",
    `/v1/brands/${encodeURIComponent(brandId)}/kits`,
  ).then((k) => k ?? []);
}

export function buildKit(
  brandId: string,
  req: BuildKitRequest,
): Promise<KitResponse> {
  return request<KitResponse>(
    "POST",
    `/v1/brands/${encodeURIComponent(brandId)}/kit`,
    req,
  );
}

// downloadKit fetches the kit tar with the operator's bearer and triggers a
// browser download (an <a href> cannot carry the Authorization header).
export async function downloadKit(
  kitID: string,
  filename: string,
): Promise<void> {
  const token = await bearer();
  const resp = await fetch(`/v1/kits/${encodeURIComponent(kitID)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new ApiError(resp.status, await errorMessage(resp));
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so the browser has started the download — an immediate revoke
  // can cancel the download in some engines.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
